import type { ReadonlyPropertyFilters } from './filterDefinitions';
import type { PropertyFilters } from './filters';
import type { LocationSlugFilters } from '~~/core/ports/repositories';

import { deduplicateRegions } from './deduplicateRegions';
import {
	normalizePropertyFilters,
	projectPropertyFilters
} from './filterDefinitions';

export const DEFAULT_LOCATION_SLUG = 'sao-paulo-sp-brasil';

export function copyPropertyFilters(
	filters: ReadonlyPropertyFilters
): PropertyFilters {
	const result: PropertyFilters = {};
	for (const [key, value] of Object.entries(filters))
		Reflect.set(result, key, Array.isArray(value) ? value.slice() : value);
	return result;
}

export function preparePropertySearchFilters({
	filters,
	includeH3Clusters,
	target = 'listing'
}: {
	filters: ReadonlyPropertyFilters;
	includeH3Clusters?: boolean;
	target?: 'listing' | 'clusters';
}): PropertyFilters {
	const result = projectPropertyFilters(filters, target);
	const hasCoordinates =
		typeof result.poiLocationLat === 'number' &&
		typeof result.poiLocationLng === 'number';
	if (hasCoordinates) {
		result.addressSlug = undefined;
		result.locationSlug = undefined;
	}
	if (
		target === 'listing' &&
		(filters.addressSlug || hasCoordinates) &&
		(!filters.sortBy || filters.sortBy === 'default_ordenation')
	)
		result.sortBy = 'distance';
	if (result.h3Ids?.length) result.bounds = undefined;
	if (includeH3Clusters) result.includeH3Clusters = true;
	return normalizePropertyFilters(result);
}

export type LocationFiltersInput = Omit<
	PropertyFilters,
	'states' | 'cities' | 'regions' | 'propertyTypes' | 'features'
> & {
	states?: string[] | string;
	cities?: string[] | string;
	regions?: string[] | string;
	propertyTypes?:
		| NonNullable<PropertyFilters['propertyTypes']>
		| NonNullable<PropertyFilters['propertyTypes']>[number];
	features?:
		| NonNullable<PropertyFilters['features']>
		| NonNullable<PropertyFilters['features']>[number];
};

export function createPropertyLocationFilters({
	states,
	cities,
	regions,
	propertyTypes,
	features,
	...rest
}: LocationFiltersInput): PropertyFilters {
	const normalize = <T>(value: T | T[] | undefined): T[] =>
		value ? (Array.isArray(value) ? value : [value]).filter(Boolean) : [];
	const result: PropertyFilters = {
		states: normalize(states),
		cities: normalize(cities),
		regions: normalize(regions),
		transactionType: 'sell',
		...rest
	};
	const types = normalize(propertyTypes);
	const amenities = normalize(features);
	if (types.length) result.propertyTypes = types;
	if (amenities.length) result.features = amenities;
	return result;
}

function withFirst(first: string | null | undefined, rest: string[] = []) {
	return [...new Set([...(first ? [first] : []), ...rest])];
}

// The listing API resolves a slug into names; consumers read them as filters.
export function mergeResolvedLocation(
	filters: PropertyFilters,
	location?: LocationSlugFilters
): PropertyFilters {
	if (!location) return filters;
	const next = copyPropertyFilters(filters);
	const regions = deduplicateRegions(withFirst(location.region, next.regions));
	const cities = withFirst(location.city, next.cities);
	if (regions.length) next.regions = regions;
	if (cities.length) next.cities = cities;
	next.states = withFirst(location.state, next.states);
	if (location.fullAddress) next.addressSlug = location.fullAddress;
	if (
		typeof location.poiLocationLat === 'number' &&
		typeof location.poiLocationLng === 'number'
	) {
		next.poiLocationLat = location.poiLocationLat;
		next.poiLocationLng = location.poiLocationLng;
	}
	return next;
}
